import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Container,
    Grid,
    Card,
    CardContent,
    Link,
    Avatar,
    Paper,
    ClickAwayListener,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from '@mui/material';
import { Global, css } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import InquiryImage from '@/assets/images/inquiry.webp';
import LoginImage from '@/assets/images/login.webp';
import ViewDetailImage from '@/assets/images/viewDetail.webp';
import CenturyOldStyleFont from '@/assets/fonts/century-old-style-std-regular.otf';
import { logOut, vendorLogOut } from '@/services/auth.service';
import { isLogined } from '@/services/mallUser.service';
import { useLoginFormDialogStore } from '@/store/loginFormDialogStore';
import { useVendorLoginDialogStore } from '@/store/vendorLoginDialogStore';
import { useUserStore } from '@/store/userStore';
import { useVendorStore } from '@/store/vendorStore';
import config from '@/config/config';
import Shop3LoginDialog from '@/components/Dialog/Shop3LoginDialog';
import VendorLoginDialog from '@/components/Dialog/VendorLoginDialog';

// 탑 네비게이션 컴포넌트
export const TopNavigation = () => {
    const navigate = useNavigate();
    const { vendorPath } = useParams();
    const { setIsOpen } = useLoginFormDialogStore((state) => state.action);
    const { setIsOpen: setVendorLoginOpen } = useVendorLoginDialogStore();
    const vendorAccessToken = useVendorStore((state) => state.accessToken);
    const vendorInfo = useVendorStore((state) => state.vendorInfo);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isVendorLoggedIn, setIsVendorLoggedIn] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [userEmail, setUserEmail] = useState<string>('');

    useEffect(() => {
        // 로그인 상태 확인
        setIsLoggedIn(isLogined());
        setIsVendorLoggedIn(!!vendorAccessToken);

        // 벤더 이메일 정보 가져오기
        if (vendorInfo?.email && vendorAccessToken) {
            setUserEmail(vendorInfo.email);
        }
    }, [vendorAccessToken, vendorInfo]);

    // 로그인 상태 변경 감지를 위한 추가 useEffect
    useEffect(() => {
        const checkLoginStatus = () => {
            setIsLoggedIn(isLogined());
            setIsVendorLoggedIn(!!vendorAccessToken);
        };

        // 주기적으로 로그인 상태 확인 (1초마다)
        const interval = setInterval(checkLoginStatus, 1000);

        return () => clearInterval(interval);
    }, [vendorAccessToken]);

    const handleUserIconClick = () => {
        // 메인 페이지에서는 seller 로그인 비활성화
        if (vendorPath) {
            if (isLoggedIn) {
                setIsDropdownOpen(!isDropdownOpen);
            } else {
                setIsOpen(true);
            }
        } else {
            // 메인 페이지에서는 vendor 로그인만 사용
            if (isVendorLoggedIn) {
                setIsDropdownOpen(!isDropdownOpen);
            } else {
                setVendorLoginOpen(true);
            }
        }
    };

    const handleProfileClose = () => {
        setIsDropdownOpen(false);
    };

    const handleLogout = async () => {
        try {
            if (isVendorLoggedIn) {
                await vendorLogOut();
            } else {
                await logOut();
            }
            setIsLoggedIn(false);
            setIsVendorLoggedIn(false);
            setUserEmail('');
            handleProfileClose();
            // 로그아웃 후 현재 경로 유지하면서 페이지 새로고침
            window.location.reload();
        } catch (error) {
            console.error('로그아웃 오류:', error);
        }
    };

    const handleAccountSettings = () => {
        navigate('/account/settings');
        handleProfileClose();
    };

    const handleAdminRedirect = () => {
        // 운영환경에 따라 관리자 페이지 URL 결정
        const adminUrl = config.url.matching_url;

        window.open(adminUrl, '_blank');
        handleProfileClose();
    };

    const getInitials = (email: string) => {
        return email.charAt(0).toUpperCase();
    };

    return (
        <AppBar
            position="sticky"
            sx={{
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                color: '#333',
            }}
        >
            <Toolbar sx={{ justifyContent: '', px: 5 }}>
                <Box
                    component="img"
                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202601/2d6577e55d24c2f340edff8bf098757f.png"
                    alt="Brand Logo"
                    onClick={() => navigate('/')}
                    sx={{
                        height: '40px',
                        cursor: 'pointer',
                        objectFit: 'contain',
                    }}
                />
                <Box flex={1} />

                {/* 메뉴 영역 */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/subscription')}
                        startIcon={<CreditCardIcon />}
                        sx={{
                            backgroundColor: '#FB6D48',
                            borderRadius: '8px',
                        }}
                    >
                        서비스플랜
                    </Button>
                    {/* 로그인/프로필 버튼 */}
                    <Box sx={{ position: 'relative' }}>
                        {(vendorPath ? isLoggedIn : isVendorLoggedIn) ? (
                            <Box sx={{ position: 'relative' }}>
                                <IconButton
                                    data-profile-button
                                    onClick={handleUserIconClick}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                        p: 0,
                                    }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            bgcolor: '#FFD1D9',
                                            color: 'rgb(151, 53, 98)',
                                            fontSize: '16px',
                                            fontWeight: 'bold',
                                            border: '2px solid #374151',
                                        }}
                                    >
                                        {userEmail ? getInitials(userEmail) : 'U'}
                                    </Avatar>
                                </IconButton>
                                {/* 화살표 버튼 - 로그인 상태일 때만 표시 */}
                                <IconButton
                                    data-profile-button
                                    onClick={handleUserIconClick}
                                    sx={{
                                        position: 'absolute',
                                        bottom: -2,
                                        right: -2,
                                        width: 18,
                                        height: 18,
                                        backgroundColor: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '50%',
                                        '&:hover': {
                                            backgroundColor: 'white',
                                        },
                                        p: 0,
                                    }}
                                >
                                    <KeyboardArrowDownIcon
                                        sx={{
                                            fontSize: 14,
                                            color: '#6b7280',
                                            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease-in-out',
                                        }}
                                    />
                                </IconButton>
                            </Box>
                        ) : (
                            <Button
                                onClick={() => {
                                    window.open(config.url.matching_url, '_blank');
                                }}
                                variant="contained"
                                sx={{
                                    backgroundColor: '#FB6D48',
                                    borderRadius: '8px',
                                }}
                            >
                                로그인
                            </Button>
                        )}

                        {/* 프로필 드롭다운 */}
                        {isDropdownOpen && (
                            <ClickAwayListener
                                onClickAway={(event) => {
                                    // 프로필 버튼 클릭은 무시 (토글을 위해)
                                    const target = event.target as Element;
                                    if (target.closest('[data-profile-button]')) {
                                        return;
                                    }
                                    handleProfileClose();
                                }}
                            >
                                <Paper
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        mt: 1,
                                        borderRadius: 1,
                                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                        border: '1px solid #e0e0e0',
                                        minWidth: 280,
                                        zIndex: 1300,
                                        overflow: 'visible',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -8,
                                            right: 24,
                                            width: 0,
                                            height: 0,
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderBottom: '8px solid white',
                                            zIndex: 1,
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: -9,
                                            right: 24,
                                            width: 0,
                                            height: 0,
                                            borderLeft: '8px solid transparent',
                                            borderRight: '8px solid transparent',
                                            borderBottom: '8px solid #e0e0e0',
                                            zIndex: 0,
                                        },
                                    }}
                                >
                                    {/* 사용자 정보 섹션 */}
                                    <Box sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    bgcolor: '#FFD1D9',
                                                    color: 'rgb(151, 53, 98)',
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {userEmail ? getInitials(userEmail) : 'V'}
                                            </Avatar>
                                            <Typography variant="body1" sx={{ fontWeight: 500, color: '#333' }}>
                                                {userEmail || '벤더'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Divider />

                                    {/* Account settings 섹션 */}
                                    <List sx={{ p: 0 }}>
                                        <ListItem
                                            component="button"
                                            onClick={handleAccountSettings}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                border: 'none',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <PersonIcon sx={{ color: '#666', fontSize: 20 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="계정 설정"
                                                primaryTypographyProps={{
                                                    fontSize: '14px',
                                                    color: '#333',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </ListItem>
                                        <ListItem
                                            component="button"
                                            onClick={handleAdminRedirect}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                border: 'none',
                                                backgroundColor: 'transparent',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                <AdminPanelSettingsIcon sx={{ color: '#666', fontSize: 20 }} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary="관리자로 이동"
                                                primaryTypographyProps={{
                                                    fontSize: '14px',
                                                    color: '#333',
                                                    fontWeight: 500,
                                                }}
                                            />
                                        </ListItem>
                                    </List>

                                    <Divider />

                                    {/* 로그아웃 버튼 */}
                                    <Box sx={{ p: 1.5 }}>
                                        <Button
                                            fullWidth
                                            variant="outlined"
                                            onClick={handleLogout}
                                            sx={{
                                                borderColor: '#ddd',
                                                color: '#333',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                py: 0.75,
                                                borderRadius: 2,
                                                '&:hover': {
                                                    borderColor: '#999',
                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                },
                                            }}
                                        >
                                            로그아웃
                                        </Button>
                                    </Box>
                                </Paper>
                            </ClickAwayListener>
                        )}
                    </Box>
                    <IconButton
                        sx={{
                            color: '#333',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                        aria-label="상담"
                        href="http://pf.kakao.com/_FxgxkUn/chat"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Box
                            component="img"
                            src={InquiryImage}
                            alt="Inquiry"
                            sx={{
                                height: 24,
                                width: 24,
                                objectFit: 'contain',
                            }}
                        />
                    </IconButton>
                </Box>
            </Toolbar>
        </AppBar>
    );
};

// 히어로 섹션 컴포넌트
const HeroSection = () => {
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handleVideoLoaded = () => {
        setIsVideoLoaded(true);
    };

    const handleVideoError = () => {
        setHasError(true);
    };

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#000',
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            {/* 폴백 이미지 - 로딩 중이거나 에러 발생 시 표시 */}
            {(!isVideoLoaded || hasError) && (
                <Box
                    component="img"
                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/8b0731d36b6510936e862f7b93e1e35c.png"
                    alt="Hero Image"
                    sx={{
                        maxWidth: '1920px',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        margin: 'auto',
                        display: !isVideoLoaded || hasError ? 'block' : 'none',
                    }}
                />
            )}

            {/* 비디오 - 로드 완료 후 표시 */}
            {!hasError && (
                <Box
                    component="video"
                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/93ba3d4828a2fd14acbb1871b571d746.mp4"
                    autoPlay
                    loop
                    muted
                    playsInline
                    onLoadedData={handleVideoLoaded}
                    onError={handleVideoError}
                    sx={{
                        maxWidth: '1920px',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                        margin: 'auto',
                        display: isVideoLoaded && !hasError ? 'block' : 'none',
                    }}
                />
            )}
        </Box>
    );
};

// 이미지 배너 섹션 컴포넌트
const ImageBannerSection = () => {
    return (
        <Box
            sx={{
                py: 12,
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            <Container maxWidth="lg">
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 4,
                        alignItems: 'stretch',
                        justifyContent: 'center',
                    }}
                >
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/PRD/202512/f847cfe5b7dd7921643cafeb5a2d565c.png"
                        alt="Banner Image 1"
                        sx={{
                            height: '314px',
                            objectFit: 'contain',
                        }}
                    />
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/PRD/202512/bc94fa9525654f8a015ac78ae4469253.gif"
                        alt="Banner Image 2"
                        sx={{
                            maxWidth: '50%',
                            height: '314px',
                            objectFit: 'contain',
                        }}
                    />
                </Box>
            </Container>
        </Box>
    );
};

// AI Studio 섹션 컴포넌트
const AIStudioSection = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedFeatureIndex, setSelectedFeatureIndex] = useState<number | null>(null);

    const handleOpenDialog = (index: number) => {
        setSelectedFeatureIndex(index);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const aiStudioFeatures = [
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/6935e2bff043c1806523ce2ffb30831b.webp',
            title: 'Face image\nMulti modal',
            description: `텍스트와 이미지를 동시에 두 정보를 결합해 분석하여 모델 이미지 생성 모델 얼굴 영역을 자동으로 인식하고, 해당 부분의 디테일을 향상시키는 모델`,
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/536465b09b9255f96434eabc5393e3c1.webp',
            title: 'Parameter Tuning\nPrompt Engineering',
            description: `얼굴과 체형은 변하지 않고 모델이 동작과 포즈만 변경 얼굴이나 이미지 손상이 이루어 지지 않으며 옷의 형태도 손상 되지 않음`,
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/9d8a1c1e8e219e9d7a3fdf8e3cff76e5.webp',
            title: 'Image Segmentation\nAI Background',
            description: `다양한 배경을 생성하여 이미지 세그먼트 기술을 통해 생성된 AI 모델의 동작이 자연스럽게 배경에 입혀질 수 있도록 만들어 줍니다.`,
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202509/f3d7eb4bddd10c3d2ff3c724af697529.webp',
            title: 'AI Fashion Rendering',
            description: `패션 상품을 AI가 스타일 있게 연출하고 보여주는 이미지 생성 기술 옷을 옷걸이나 마네킹에 자연스럽게 배치하거나, 배경, 조명, 소품까지 조화롭게 만들어줌`,
        },
    ];

    return (
        <Box sx={{ py: 12, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
                {/* 헤더 섹션 */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        sx={{
                            color: '#333',
                            mb: 3,
                            fontSize: '2.5rem',
                        }}
                    >
                        SHOP3 AI Studio
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                            mb: 1,
                        }}
                    >
                        샵쓰리는 온에스트레이션과 방식을 적용해 피팅을 진행합니다.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                        }}
                    >
                        여러 AI 공정을 정교하게 조율하여 더욱 완성도 높은 결과로 만들어 드립니다.
                    </Typography>
                </Box>

                {/* 카드 그리드 컨테이너 */}
                <Box
                    sx={{
                        backgroundColor: '#2f3438',
                        borderRadius: '30px',
                        p: 4,
                        position: 'relative',
                    }}
                >
                    <Grid container spacing={1}>
                        {aiStudioFeatures.map((feature, index) => (
                            <Grid size={3} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        backgroundColor: '#35393c',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {/* 이미지 섹션 */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            aspectRatio: '297/431',
                                            p: 2,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={feature.image}
                                            alt={feature.title}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    </Box>

                                    {/* 컨텐츠 섹션 */}
                                    <CardContent
                                        sx={{
                                            p: 3,
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            minHeight: '180px',
                                            position: 'relative',
                                            flex: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minHeight: '70px',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'center',
                                                mb: 2,
                                            }}
                                        >
                                            <Typography
                                                variant="h3"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    fontSize: '1.1rem',
                                                    whiteSpace: 'pre-line',
                                                    textAlign: 'center',
                                                    lineHeight: 1.1,
                                                }}
                                            >
                                                {feature.title}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                minHeight: '80px',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                justifyContent: 'center',
                                                mb: 2,
                                                flex: 1,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#ccc',
                                                    lineHeight: 1.5,
                                                    fontSize: '0.8rem',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </Box>
                                        {/* 3점 아이콘 */}
                                        <Box height={'20px'} />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                left: 0,
                                                display: 'flex',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                                pb: 1,
                                                width: '100%',
                                            }}
                                        >
                                            <Box
                                                component={'img'}
                                                src={ViewDetailImage}
                                                alt="View Details"
                                                onClick={() => handleOpenDialog(index)}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                    '&:hover': {
                                                        cursor: 'pointer',
                                                    },
                                                    mb: 1,
                                                }}
                                            />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {/* 오버레이 다이얼로그 */}
                    {openDialog && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                            }}
                            onClick={handleCloseDialog}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#2f3438',
                                    borderRadius: '30px',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {/* 상단 헤더 */}
                                <Box
                                    sx={{
                                        backgroundColor: '#2f3438',
                                        color: 'white',
                                        p: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    {/* 닫기 버튼 */}
                                    <IconButton
                                        onClick={handleCloseDialog}
                                        sx={{
                                            position: 'absolute',
                                            right: 16,
                                            top: 16,
                                            color: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>

                                    {/* 별점 */}
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                            mb: 1,
                                            letterSpacing: 2,
                                        }}
                                    >
                                        ★★★★★
                                    </Typography>

                                    {/* 제목 */}
                                    <Typography
                                        variant="h1"
                                        sx={{
                                            fontWeight: 'normal',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {selectedFeatureIndex !== null
                                            ? aiStudioFeatures[selectedFeatureIndex].title
                                            : ''}
                                    </Typography>
                                </Box>

                                {/* 하단 컨텐츠 영역 */}
                                <Box
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        p: 4,
                                        flex: 1,
                                        overflowY: 'auto',
                                    }}
                                    data-hint="하단 컨텐츠 영역"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 3,
                                        }}
                                    >
                                        {(() => {
                                            switch (selectedFeatureIndex) {
                                                case 0:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                gap: 1,
                                                                alignItems: 'flex-start',
                                                            }}
                                                        >
                                                            {/* 왼쪽 */}
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flex: 0.8,
                                                                    flexDirection: 'row',
                                                                    alignItems: 'flex-start',
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/85e28ad265a7c078e1da5cdf7983dddb.png"
                                                                    alt="Face Image 멀티 모달"
                                                                    sx={{
                                                                        width: '40%',
                                                                        height: '250px',
                                                                        objectFit: 'contain',
                                                                        aspectRatio: '1/1',
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        flex: 1,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        justifyContent: 'flex-start',
                                                                        alignItems: 'flex-start',
                                                                        ml: 2,
                                                                        mt: 7,
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="h1"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                mb: 2,
                                                                                color: '#333',
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            Face Image 멀티 모달
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: '#666',
                                                                                lineHeight: 1.8,
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            모델은 멀티모달 방식을 통해 텍스트와
                                                                            이미지를 동시에 입력받아 두 정보를 결합해
                                                                            분석합니다. 이를 기반으로 한국 고객들이
                                                                            선호하는 모델 이미지를 생성합니다.
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>

                                                            {/* 오른쪽 */}
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    flex: 1,
                                                                    flexDirection: 'row',
                                                                    alignItems: 'flex-start',
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/12de23c1e0dacbdbde08375db418430a.png"
                                                                    alt="Face Detailer"
                                                                    sx={{
                                                                        width: '42%',
                                                                        height: '550px',
                                                                        objectFit: 'contain',
                                                                    }}
                                                                />
                                                                <Box
                                                                    sx={{
                                                                        flex: 1,
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        justifyContent: 'center',
                                                                        alignItems: 'flex-start',
                                                                        ml: 2,
                                                                        mt: 7,
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography
                                                                            variant="h1"
                                                                            sx={{
                                                                                fontWeight: 'bold',
                                                                                mb: 2,
                                                                                color: '#333',
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            Face Detailer
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: '#666',
                                                                                lineHeight: 1.8,
                                                                                textAlign: 'left',
                                                                            }}
                                                                        >
                                                                            모델은 얼굴 영역을 자동으로 인식하고, 해당
                                                                            부분의 해상도와 선명도, 디테일을 개선하여
                                                                            더욱 선명하고 자연스러운 이미지를 만들어
                                                                            줍니다.
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                case 1:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {/* 왼쪽 이미지 */}
                                                            <Box
                                                                component="img"
                                                                src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/8937233180c06833231761340213ebee.webp"
                                                                alt="Face Swap Model"
                                                                sx={{
                                                                    width: '45%',
                                                                    height: 'auto',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />

                                                            {/* 오른쪽 텍스트 영역 */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {/* Face Swap */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Face Swap
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        생성된 모델의 얼굴이 자연스럽게 그대로
                                                                        유지되도록 합니다.
                                                                    </Typography>
                                                                </Box>

                                                                {/* Parameter Tuning Prompt Engineering */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Parameter Tuning Prompt Engineering
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        모델 자체를 변경하지 않고 입력 문장(프롬프트)을
                                                                        설계하여 반응을 조정하는 기법입니다. 이는 모델의
                                                                        학습이나 추론 과정에서 하이퍼파라미터나 내부
                                                                        설정값을 조정하는 방식으로 이루어집니다. 이
                                                                        과정을 통해 모델의 동작은 달라질 수 있지만,
                                                                        얼굴이나 이미지가 손상되지 않고 옷의 형태 또한
                                                                        그대로 유지되도록 합니다.
                                                                    </Typography>
                                                                </Box>

                                                                {/* Identity-Preserving Pose Generation */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Identity-Preserving Pose Generation
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        신원 보존 포즈 생성’은 얼굴이나 체형 등 모델의
                                                                        핵심 특성을 그대로 유지하면서, 동작이나 포즈만
                                                                        자연스럽게 바꿔주는 기술입니다. 이 과정에서
                                                                        모델의 움직임은 달라지지만, 얼굴이나 이미지가
                                                                        손상되지 않고 옷의 형태도 온전히 보존됩니다.
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                case 2:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {/* 왼쪽 이미지 */}
                                                            <Box
                                                                component="img"
                                                                src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/fe258d9b363469967ac72f3679400176.webp"
                                                                alt="Case 2 Image"
                                                                sx={{
                                                                    width: '53%',
                                                                    height: 'auto',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />

                                                            {/* 오른쪽 텍스트 영역 */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        Image Segmentation AI Background
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        다양한 배경을 생성하고, 이미지 세그먼트 기술을
                                                                        활용하여 생성된 AI 모델의 동작이 더욱 자연스럽게
                                                                        배경과 어우러지도록 구현합니다.
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                case 3:
                                                    return (
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                flexDirection: 'row',
                                                                alignItems: 'center',
                                                                gap: 4,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            {/* 왼쪽 이미지 */}
                                                            <Box
                                                                component="img"
                                                                src="https://d1vb419ubgm46b.cloudfront.net/PRD/202509/ea280f4e3e10b763c9311738fe9d034f.webp"
                                                                alt="Case 3 Image"
                                                                sx={{
                                                                    width: '63%',
                                                                    height: 'auto',
                                                                    objectFit: 'contain',
                                                                }}
                                                            />

                                                            {/* 오른쪽 텍스트 영역 */}
                                                            <Box
                                                                sx={{
                                                                    flex: 1,
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 4,
                                                                }}
                                                            >
                                                                {/* 단일 단락 */}
                                                                <Box>
                                                                    <Typography
                                                                        variant="h2"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            mb: 1.5,
                                                                            color: '#333',
                                                                        }}
                                                                    >
                                                                        AI Fashion Rendering
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#666',
                                                                            lineHeight: 1.8,
                                                                        }}
                                                                    >
                                                                        AI가 패션 상품을 세련되게 연출해 보여주는 이미지
                                                                        생성 기술입니다. 옷을 옷걸이나 마네킹에
                                                                        자연스럽게 배치하고, 배경·조명·소품까지 조화롭게
                                                                        구성하여 더욱 감각적인 이미지를 완성합니다.
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                            }
                                            return <></>;
                                        })()}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

// SHOP3 Cut Type 섹션 컴포넌트
const CutTypeSection = () => {
    const cutTypes = [
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/93f30d6541fb8024053a103d0877fd90.webp',
            title: '모델컷',
            description:
                '상품의 매력을 가장 잘 살려줄 1000% 이상의 다양한 AI 모델과 상품을 매칭해 가장 멋나게 핏합니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/94dc7ef211565a71db78e0088485cd8b.webp',
            title: '배경컷',
            description:
                '내 상품은 입소의 시간과 구애를 받지 않고 어디서든 어울려며, 색감도 반한 연출을 생생 가지를 제공합니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/86d5389619901fa04da8e892872de460.webp',
            title: '소품컷',
            description:
                '고객은 제품이 실제로 어울리는지 미리 확인하고 싶어합니다. 다양한 소품 매칭은 고객의 이용을 증진시킵니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/98ff98b907dac2186370c147d9abc3ee.webp',
            title: '옷걸이컷',
            description: '조잡한 촬영을 사라지게 합니다. 깔끔한 눈높이 각도로도 소장 욕구를 자극합니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/22f6a1a54e5f9fc769656f7f558e2b53.webp',
            title: '세팅컷',
            description: '다양한 방식으로 물건적 상황과 것이 안정감과 구매 가치를 높입니다.',
        },
        {
            image: 'https://d1vb419ubgm46b.cloudfront.net/DEV/202509/79f4fd2a5b638306f27c931a4ae4efd4.webp',
            title: '포커스컷',
            description: '원단의 질감과 디테일한 디자인을 생생하게 전달합니다.',
        },
    ];

    return (
        <Box sx={{ py: 12, backgroundColor: 'white' }}>
            <Container maxWidth="lg">
                {/* 헤더 섹션 */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 'bold',
                            color: '#333',
                            mb: 3,
                            fontSize: '2.5rem',
                        }}
                    >
                        SHOP3 Cut Type
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: '#666',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                        }}
                    >
                        샵쓰리에는 6가지의 컷이 있습니다.
                    </Typography>
                </Box>

                {/* 카드 그리드 컨테이너 */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 10,
                        padding: '0 7%',
                    }}
                >
                    {cutTypes.map((cutType, index) => (
                        <Card
                            key={index}
                            sx={{
                                height: '100%',
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={cutType.image}
                                    alt={cutType.title}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            </Box>
                            <CardContent
                                sx={{
                                    p: { xs: 0, md: 2 },
                                    textAlign: 'center',
                                    border: 'none',
                                    boxShadow: 'none',
                                }}
                            >
                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: '#333',
                                        mb: 1.5,
                                    }}
                                >
                                    {cutType.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#666',
                                        lineHeight: 1.6,
                                        fontSize: '0.9rem',
                                    }}
                                >
                                    {cutType.description}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Container>
        </Box>
    );
};

// 섹션 3 (플레이스홀더)
const Section3 = () => {
    return (
        <Box
            sx={{
                py: 10,
                background:
                    'url("https://d1vb419ubgm46b.cloudfront.net/DEV/202509/e3c640b4693b418230fd283441b50f3e.webp") center center / cover no-repeat fixed',
                backgroundSize: 'cover',
                minHeight: '1300px',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="lg">
                {/* 헤더 섹션 */}
                <Box sx={{ textAlign: 'center', mb: 6, mt: 10 }}>
                    <Typography
                        variant="h3"
                        component="h2"
                        sx={{
                            fontWeight: 'bold',
                            color: 'white',
                            mb: 3,
                            fontSize: '2.5rem',
                        }}
                    >
                        SHOP3 Post Hub Mobile
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'white',
                            fontSize: '1.1rem',
                            lineHeight: 1.6,
                        }}
                    >
                        AI로 만들어진 모델 사진을 자동으로 SNS(인스타그램, 틱톡, 카카오스토리)에 공유됩니다
                    </Typography>
                </Box>
                <Box display={'flex'} justifyContent={'center'} gap={3}>
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/340047cf9a30ad2b89ac89f7d872fcbc.webp"
                        alt="Section 1 Image"
                        sx={{ width: '48px', height: '48px' }}
                    />
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/8b2611f9eadd07441f8fce57972e7291.webp"
                        alt="Section 1 Image"
                        sx={{ width: '48px', height: '48px' }}
                    />
                    <Box
                        component="img"
                        src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/831bd83a996c65f86c355efe0cb6833c.webp"
                        alt="Section 1 Image"
                        sx={{ width: '48px', height: '48px' }}
                    />
                </Box>
            </Container>
            <Box
                component="img"
                position={'relative'}
                src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/dd006d672c3f3346f0fc8813463cd65b.webp"
                alt="Section 1 Image"
                sx={{ width: '110%', left: '-5%', mt: 5 }}
            />
        </Box>
    );
};
// 섹션 4 (플레이스홀더)
const Section4 = () => {
    return (
        <Box sx={{ py: 10, backgroundColor: '#f0f0f0' }}>
            <Container maxWidth="lg">
                <Typography variant="h4" align="center" color="#666">
                    궁금하신 점은 언제든지 문의해 주세요. 전문 상담원이 친절하게 안내해 드리겠습니다.
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Button
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            px: 3,
                            py: 1.5,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#f9f9f9',
                                border: '1px solid #bbb',
                            },
                        }}
                        href="http://pf.kakao.com/_FxgxkUn/chat"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                background: 'transparent',
                            }}
                            component={'img'}
                            src="https://d1vb419ubgm46b.cloudfront.net/DEV/202509/83588f4d9c45901c96f30ade88ef5086.webp"
                        />
                        <Typography
                            sx={{
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: '#333',
                            }}
                        >
                            상담 요청하기
                        </Typography>
                        <Typography sx={{ color: '#666', fontSize: '1.2rem' }}>›</Typography>
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

// 푸터 섹션 컴포넌트
const FooterSection = () => {
    return (
        <Box
            sx={{
                backgroundColor: '#2a2a2a',
                color: 'white',
                py: 6,
                marginTop: 'auto',
            }}
        >
            <Container maxWidth="lg">
                {/* 상단 링크 영역 */}
                <Box
                    sx={{
                        display: 'flex',
                        gap: 3,
                        mb: 4,
                        fontSize: '0.95rem',
                    }}
                >
                    <Link
                        href="https://terms.flowing.tel/terms.html"
                        target="_blank"
                        sx={{
                            color: '#ccc',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'white',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        운영정책
                    </Link>
                    <Link
                        href="https://terms.flowing.tel/terms.html"
                        target="_blank"
                        sx={{
                            color: '#ccc',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'white',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        서비스 약관
                    </Link>
                    <Link
                        href="https://terms.flowing.tel/terms.html"
                        target="_blank"
                        sx={{
                            color: '#ccc',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'white',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        개인정보 보호방침
                    </Link>
                </Box>

                {/* 회사 정보 영역 */}
                <Box
                    sx={{
                        fontSize: '0.85rem',
                        lineHeight: 1.8,
                        color: '#aaa',
                        mb: 3,
                    }}
                >
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        상호명: (주)플로잉스페이스
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>대표이사: 오석환</Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        플로잉 서비스 고객센터 이메일: help@flowing.tel
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        주소: 서울 강동구 길동 천호대로 1121 일성하이츠 205
                    </Typography>
                    <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 1 }}>
                        사업자등록번호 650-86-03029 통신판매신고: 제 2024-서울강동-1600 호 개인정보관리 책임자: 손유한
                        전화번호: 070-4504-4055 문의: help@flowing.tel
                    </Typography>
                </Box>

                {/* 저작권 영역 */}
                <Typography
                    sx={{
                        fontSize: '0.85rem',
                        color: '#888',
                    }}
                >
                    2022 Flowing Corp.
                </Typography>
            </Container>
        </Box>
    );
};

// 메인 페이지 컴포넌트
const Main2 = () => {
    const { isOpen: isVendorLoginOpen, setIsOpen: setVendorLoginOpen } = useVendorLoginDialogStore();

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Global
                styles={css`
                    @font-face {
                        font-family: 'Century Old Style';
                        src: url(${CenturyOldStyleFont}) format('opentype');
                        font-weight: normal;
                        font-style: normal;
                    }

                    * {
                        font-family: 'Century Old Style', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto',
                            'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
                    }
                `}
            />
            <TopNavigation />
            <HeroSection />
            <ImageBannerSection />
            <AIStudioSection />
            <CutTypeSection />
            <Section3 />
            <Section4 />
            <FooterSection />

            {/* Dialog 컴포넌트들 */}
            <Shop3LoginDialog />
            <VendorLoginDialog isOpen={isVendorLoginOpen} onClose={() => setVendorLoginOpen(false)} />
        </Box>
    );
};

export default Main2;
